import { useCallback, useEffect, useState } from "react";
import { Home, Plus, Loader2, Eye, AlertTriangle, ShieldAlert, Sparkles, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { RiskBadge } from "../components/RiskBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TYPE_META = {
  observation: { label: "Observation", icon: Eye, cls: "bg-[#D6E0D9]/40 text-[#2E5C50]" },
  incident: { label: "Incident", icon: ShieldAlert, cls: "bg-[#FDF3E1] text-[#C48231]" },
  hazard: { label: "Hazard", icon: AlertTriangle, cls: "bg-[#FCEAE8] text-[#B54D40]" },
  improvement: { label: "Improvement", icon: TrendingUp, cls: "bg-[#E6F0EB] text-[#34745C]" },
};

const EMPTY = { client_id: "", date: new Date().toLocaleDateString("en-CA"), entry_type: "observation", risk_level: "low", notes: "", photo_base64: null };

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]";

export default function PropertyPage() {
  const { user } = useAuth();
  const isFamily = user.role === "family";
  const canAnalyze = user.role === "agency" || user.role === "admin";
  const [logs, setLogs] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(isFamily ? user.id : "");
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(() => {
    const params = !isFamily && selectedClient ? { client_id: selectedClient } : {};
    return api.get("/property-logs", { params }).then((r) => setLogs(r.data));
  }, [isFamily, selectedClient]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isFamily) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [isFamily]);

  useEffect(() => {
    const cid = isFamily ? user.id : selectedClient;
    if (!cid || user.role === "subcontractor") {
      setReport(null);
      return;
    }
    api.get(`/property-history/${cid}/report`).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [isFamily, selectedClient, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return setForm({ ...form, photo_base64: null });
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo too large (max 2 MB)");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/property-logs", { ...form, client_id: isFamily ? user.id : form.client_id });
      toast.success("Entry recorded");
      setOpen(false);
      setForm({ ...EMPTY, date: new Date().toLocaleDateString("en-CA") });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await api.delete(`/property-logs/${entry.id}`);
      toast.success("Entry removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove entry");
    }
  };

  const analyze = async () => {
    const cid = isFamily ? user.id : selectedClient;
    if (!cid) return toast.error("Select a client first");
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/property-history/${cid}/analyze`);
      setReport(data);
      toast.success("Environment patterns analyzed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const r = report?.report;

  return (
    <Layout>
      <div data-testid="property-page" className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Property History</p>
            <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
              {isFamily ? <>Your <span className="font-bold text-[#2E5C50]">home environment</span></> : <>Client <span className="font-bold text-[#2E5C50]">environments</span></>}
            </h1>
            <p className="mt-2 text-[#57605A] max-w-2xl">
              Track home conditions, safety risks and incidents over time. Nexari detects repeated issues and hazard patterns that may affect care.
            </p>
          </div>
          <button
            data-testid="add-property-log-btn"
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add entry
          </button>
        </div>

        {!isFamily && (
          <select
            data-testid="property-client-select"
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className={`${inputCls} max-w-sm`}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-3">
            <h2 className="text-lg font-heading font-semibold">Timeline</h2>
            {logs === null ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
              </div>
            ) : logs.length === 0 ? (
              <div data-testid="empty-property-logs" className="bg-white border border-[#E8E6E1] rounded-lg p-12 text-center">
                <Home className="w-8 h-8 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
                <p className="mt-3 text-[#57605A]">No entries yet. Record an observation or load demo data from the dashboard.</p>
              </div>
            ) : (
              logs.map((e, i) => {
                const meta = TYPE_META[e.entry_type] || TYPE_META.observation;
                return (
                  <div
                    key={e.id}
                    data-testid={`property-log-row-${e.id}`}
                    className="fade-in-up bg-white border border-[#E8E6E1] rounded-lg p-5 flex gap-4"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${meta.cls}`}>
                      <meta.icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm text-[#1C211F]">{meta.label}</span>
                        <RiskBadge level={e.risk_level} />
                        {e.is_demo && <span className="text-xs text-[#C07A60] font-semibold">DEMO</span>}
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-[#1C211F]">{e.notes}</p>
                      {e.photo_base64 && (
                        <img src={e.photo_base64} alt="Property" className="mt-3 rounded-md border border-[#E8E6E1] max-h-44 object-cover" />
                      )}
                      <p className="mt-2 text-xs text-[#57605A]">
                        {e.client_name} · {new Date(`${e.date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · recorded by {e.recorded_by_name} ({e.recorded_by_role})
                      </p>
                    </div>
                    {(e.recorded_by_id === user.id || user.role === "admin") && (
                      <button
                        data-testid={`delete-property-log-${e.id}`}
                        onClick={() => remove(e)}
                        className="self-start p-1.5 text-[#57605A] hover:text-[#B54D40] transition-colors duration-150"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {user.role !== "subcontractor" && (
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-heading font-semibold">Pattern analysis</h2>
                {canAnalyze && (
                  <button
                    data-testid="analyze-property-btn"
                    onClick={analyze}
                    disabled={analyzing || (!isFamily && !selectedClient)}
                    className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                    {analyzing ? "Analyzing…" : "Analyze patterns"}
                  </button>
                )}
              </div>
              {!r ? (
                <div className="bg-white border border-[#E8E6E1] rounded-xl p-8 text-center">
                  <Sparkles className="w-7 h-7 text-[#C9CFC9] mx-auto" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-[#57605A]">
                    {canAnalyze
                      ? isFamily || selectedClient
                        ? "Run Nexari's AI to detect repeated issues and safety patterns in this home."
                        : "Select a client to view or run their environment analysis."
                      : "Your agency runs environment pattern analyses — the latest report appears here."}
                  </p>
                </div>
              ) : (
                <div data-testid="property-report" className="bg-white border border-[#E8E6E1] rounded-xl p-6 space-y-4 fade-in-up">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-[#57605A]">
                      {report.client_name} · {new Date(report.created_at).toLocaleString()} · {report.log_count} entries
                    </p>
                    <RiskBadge level={r.risk_level} />
                  </div>
                  <p className="text-sm leading-relaxed text-[#1C211F]">{r.summary}</p>
                  {(r.repeated_issues || []).length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Repeated issues</h3>
                      <ul className="mt-2 space-y-1.5">
                        {r.repeated_issues.map((iss, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <AlertTriangle className="w-4 h-4 text-[#C48231] shrink-0 mt-0.5" strokeWidth={1.5} /> {iss}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(r.safety_hazards || []).length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Safety hazards</h3>
                      <div className="mt-2 space-y-2">
                        {r.safety_hazards.map((h, i) => (
                          <div key={i} className={`border rounded-lg p-3 text-sm ${
                            h.severity === "high" ? "bg-[#FCEAE8] border-[#B54D40]/20" : h.severity === "low" ? "bg-[#E6F0EB] border-[#34745C]/20" : "bg-[#FDF3E1] border-[#C48231]/20"
                          }`}>
                            <span className="text-xs font-bold uppercase tracking-wider mr-2">{h.severity}</span>
                            {h.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(r.recommended_actions || []).length > 0 && (
                    <div>
                      <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">Recommended actions</h3>
                      <ol className="mt-2 space-y-1.5">
                        {r.recommended_actions.map((aIt, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                            <span className="w-5 h-5 bg-[#2E5C50] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                            {aIt}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add environment entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 mt-2">
            {!isFamily && (
              <select data-testid="log-client-select" required value={form.client_id} onChange={set("client_id")} className={inputCls}>
                <option value="">Select a client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="log-date-input" required type="date" value={form.date} onChange={set("date")} className={inputCls} />
              <select data-testid="log-type-select" value={form.entry_type} onChange={set("entry_type")} className={inputCls}>
                {Object.entries(TYPE_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
              <select data-testid="log-risk-select" value={form.risk_level} onChange={set("risk_level")} className={inputCls}>
                <option value="low">Low risk</option>
                <option value="medium">Medium risk</option>
                <option value="high">High risk</option>
              </select>
            </div>
            <textarea data-testid="log-notes-input" required minLength={1} placeholder="What did you observe? Conditions, hazards, changes…" rows={3} value={form.notes} onChange={set("notes")} className={inputCls} />
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Photo (optional, max 2 MB)</label>
              <input data-testid="log-photo-input" type="file" accept="image/*" onChange={onPhoto} className={`${inputCls} mt-1`} />
              {form.photo_base64 && <img src={form.photo_base64} alt="Preview" className="mt-2 rounded-md border border-[#E8E6E1] max-h-32" />}
            </div>
            <button
              data-testid="save-property-log-btn"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-3 font-medium disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
              Save entry
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
